import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import './App.css';
import Card from './components/Card';
import Modal from './components/Modal';
import Login from './components/Login';
import AdminPanel from './components/AdminPanel';
import StructureEditor from './components/StructureEditor';
import { io } from 'socket.io-client';

interface User {
  username: string;
  is_admin: number;
}

interface Profile {
  id: number;
  name: string;
  percentage?: number;
}

interface Item {
  id: number;
  label: string;
  state: number;
}

interface Section {
  id: number;
  title: string;
  items: Item[];
}

const isProd = process.env.NODE_ENV === 'production';
const API_URL = isProd ? '/api' : `http://${window.location.hostname}:4658/api`;
const socket = io(isProd ? '/' : `http://${window.location.hostname}:4658`);

function App() {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [user, setUser] = useState<User | null>(JSON.parse(localStorage.getItem('user') || 'null'));
  
  const [view, setView] = useState<'dashboard' | 'admin' | 'settings' | 'structure'>('dashboard');
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [activeProfile, setActiveProfile] = useState<Profile | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [newProfileName, setNewProfileName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(socket.connected);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<number | null>(null);

  const handleLogout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setView('dashboard');
    setActiveProfile(null);
  }, []);

  const showError = useCallback((msg: string) => {
    setError(msg);
    setTimeout(() => setError(null), 3000);
  }, []);

  const fetchProfiles = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(response.data);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      if (err.response?.status === 401 || err.response?.status === 403) {
        handleLogout();
      } else {
        showError('Verbindung zum Server fehlgeschlagen');
      }
    }
  }, [token, handleLogout, showError]);

  useEffect(() => {
    if (token) {
      fetchProfiles();
    }
  }, [token, fetchProfiles]);

  useEffect(() => {
    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    socket.on('progress_updated', (data) => {
      // Update local view if profile is open
      if (activeProfile && activeProfile.id === data.profileId) {
        setSections(prev => prev.map(section => ({
          ...section,
          items: section.items.map(item => 
            item.id === data.templateId ? { ...item, state: data.state } : item
          )
        })));
      }
      // Refresh profiles list to update percentages in dashboard
      fetchProfiles();
    });

    socket.on('profiles_updated', () => {
      fetchProfiles();
    });

    socket.on('structure_updated', () => {
      if (activeProfile) {
        fetchProfileData(activeProfile);
      }
    });

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('progress_updated');
      socket.off('profiles_updated');
      socket.off('structure_updated');
    };
  }, [activeProfile, fetchProfiles]);

  const fetchProfileData = async (profile: Profile) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/profiles/${profile.id}/data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(response.data);
      setActiveProfile(profile);
      setLoading(false);
      window.scrollTo(0, 0);
    } catch (err) {
      setLoading(false);
      showError('Daten konnten nicht geladen werden');
    }
  };

  const handleCreateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProfileName.trim()) return;
    try {
      const response = await axios.post(`${API_URL}/profiles`, { name: newProfileName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewProfileName('');
    } catch (err) {
      showError('Fehler beim Erstellen');
    }
  };

  const openDeleteModal = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setProfileToDelete(id);
    setIsModalOpen(true);
  };

  const confirmDeleteProfile = async () => {
    if (profileToDelete === null) return;
    try {
      await axios.delete(`${API_URL}/profiles/${profileToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(profiles.filter(p => p.id !== profileToDelete));
      if (activeProfile?.id === profileToDelete) setActiveProfile(null);
      setIsModalOpen(false);
      setProfileToDelete(null);
    } catch (err) {
      showError('Fehler beim Löschen');
      setIsModalOpen(false);
    }
  };

  const handleUpdateProgress = async (templateId: number, newState: number) => {
    if (!activeProfile) return;
    try {
      await axios.post(`${API_URL}/progress`, {
        profileId: activeProfile.id,
        templateId,
        state: newState
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSections(prev => prev.map(section => ({
        ...section,
        items: section.items.map(item => 
          item.id === templateId ? { ...item, state: newState } : item
        )
      })));
    } catch (err) {
      showError('Fehler: Keine Verbindung zur Datenbank!');
      throw err;
    }
  };

  const handleRenameProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeProfile || !editedName.trim()) return;
    try {
      await axios.put(`${API_URL}/profiles/${activeProfile.id}`, { name: editedName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveProfile({ ...activeProfile, name: editedName });
      setProfiles(prev => prev.map(p => p.id === activeProfile.id ? { ...p, name: editedName } : p)
        .sort((a, b) => a.name.localeCompare(b.name)));
      setIsEditingName(false);
    } catch (err) {
      showError('Fehler beim Umbenennen');
    }
  };

  const handleLogin = (newToken: string, newUser: User) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  const handleChangePassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const newPass = (form.elements.namedItem('newPassword') as HTMLInputElement).value;
    try {
      await axios.post(`${API_URL}/change-password`, { newPassword: newPass }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Passwort erfolgreich geändert!');
      form.reset();
      setView('dashboard');
    } catch (err) {
      showError('Fehler beim Ändern des Passworts');
    }
  };

  const calculateCurrentPercentage = () => {
    let totalItems = 0;
    let totalScore = 0;
    sections.forEach(s => {
      s.items.forEach(i => {
        totalItems++;
        totalScore += i.state;
      });
    });
    return totalItems > 0 ? Math.round((totalScore / (totalItems * 4)) * 100) : 0;
  };

  if (!token || !user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="app-container">
      {!isConnected && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0,0,0,0.85)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          color: 'white',
          padding: '20px'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '20px' }}>⚠️</div>
          <h2 style={{ marginBottom: '10px' }}>Verbindung unterbrochen</h2>
          <p style={{ color: 'var(--text-secondary)' }}>
            Die Verbindung zum Server wurde verloren.<br/>
            Bitte warte, während wir versuchen, sie wiederherzustellen...
          </p>
        </div>
      )}
      <div className="header" style={{ flexDirection: 'column', gap: '20px', alignItems: 'center', padding: '20px' }}>
        <img 
          src={`${process.env.PUBLIC_URL}/vahrschule logo.png`} 
          alt="Vahrschule Logo" 
          style={{ width: '100%', maxWidth: '200px' }} 
        />
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button className="btn" onClick={() => { setView('dashboard'); setActiveProfile(null); }}>Dashboard</button>
          <button className="btn" onClick={() => setView('settings')}>Einstellungen</button>
          {user.is_admin === 1 && (
            <>
              <button className="btn btn-secondary" onClick={() => setView('structure')}>Karten Aufbau</button>
              <button className="btn btn-secondary" onClick={() => setView('admin')}>Admin</button>
            </>
          )}
          <button className="btn btn-danger" onClick={handleLogout}>Logout ({user.username})</button>
        </div>
      </div>

      {loading && <div className="loading" style={{ position: 'fixed', top: '10px', right: '10px' }}>Lädt...</div>}

      {view === 'structure' && user.is_admin === 1 && <StructureEditor token={token!} />}

      {view === 'dashboard' && (
        <>
          {!activeProfile ? (
            <div className="profile-list">
              <div className="add-card-form">
                <h3>Neues Schüler-Profil</h3>
                <form onSubmit={handleCreateProfile} style={{ display: 'flex', gap: '10px' }}>
                  <input 
                    className="input-field"
                    style={{ marginBottom: 0 }}
                    placeholder="Name des Schülers" 
                    value={newProfileName}
                    onChange={(e) => setNewProfileName(e.target.value)}
                  />
                  <button type="submit" className="btn btn-primary">Erstellen</button>
                </form>
              </div>

              <h3>Schüler Übersicht:</h3>
              <input 
                className="input-field"
                placeholder="🔍 Schüler suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ marginBottom: '20px' }}
              />
              {profiles
                .filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
                .map(p => {
                  const percentage = p.percentage ?? 0;
                  // Calculate color: Red (0, 200, 80) to Green (120, 200, 80) in HSL
                  const hue = (percentage * 1.2); // 0% = 0 (red), 100% = 120 (green)
                  const color = `hsl(${hue}, 70%, 45%)`;
                  const radius = 18;
                  const circumference = 2 * Math.PI * radius;
                  const offset = circumference - (percentage / 100) * circumference;

                  return (
                    <div key={p.id} className="dashboard-card" onClick={() => fetchProfileData(p)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ position: 'relative', width: '54px', height: '54px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <svg width="54" height="54" style={{ transform: 'rotate(-90deg)' }}>
                            <circle
                              cx="27"
                              cy="27"
                              r="23"
                              stroke="#eee"
                              strokeWidth="4"
                              fill="transparent"
                            />
                            <circle
                              cx="27"
                              cy="27"
                              r="23"
                              stroke={color}
                              strokeWidth="4"
                              fill="transparent"
                              strokeDasharray={2 * Math.PI * 23}
                              strokeDashoffset={(2 * Math.PI * 23) - (percentage / 100) * (2 * Math.PI * 23)}
                              strokeLinecap="round"
                              style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.5s ease' }}
                            />
                          </svg>
                          <span style={{ 
                            position: 'absolute', 
                            fontSize: '0.85rem', 
                            fontWeight: 'bold',
                            color: 'white' 
                          }}>
                            {percentage}%
                          </span>
                        </div>
                        <span>{p.name}</span>
                      </div>
                      <button className="btn btn-danger btn-small" onClick={(e) => openDeleteModal(e, p.id)}>Löschen</button>
                    </div>
                  );
                })}            </div>
          ) : (
            <div className="profile-detail">
              <div className="nav-back" onClick={() => setActiveProfile(null)}>← Zurück zur Übersicht</div>
              
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginBottom: '30px' }}>
                {isEditingName ? (
                  <form onSubmit={handleRenameProfile} style={{ display: 'flex', gap: '10px' }}>
                    <input 
                      className="input-field" 
                      style={{ marginBottom: 0, fontSize: '1.2rem', textAlign: 'center' }}
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      autoFocus
                    />
                    <button type="submit" className="btn btn-primary btn-small">✓</button>
                    <button type="button" className="btn btn-secondary btn-small" onClick={() => setIsEditingName(false)}>X</button>
                  </form>
                ) : (
                  <>
                    <div style={{ position: 'relative', width: '54px', height: '54px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <svg width="54" height="54" style={{ transform: 'rotate(-90deg)' }}>
                        <circle cx="27" cy="27" r="23" stroke="#eee" strokeWidth="4" fill="transparent" />
                        <circle
                          cx="27"
                          cy="27"
                          r="23"
                          stroke={`hsl(${calculateCurrentPercentage() * 1.2}, 70%, 45%)`}
                          strokeWidth="4"
                          fill="transparent"
                          strokeDasharray={2 * Math.PI * 23}
                          strokeDashoffset={(2 * Math.PI * 23) - (calculateCurrentPercentage() / 100) * (2 * Math.PI * 23)}
                          strokeLinecap="round"
                          style={{ transition: 'stroke-dashoffset 0.5s ease, stroke 0.5s ease' }}
                        />
                      </svg>
                      <span style={{ position: 'absolute', fontSize: '0.85rem', fontWeight: 'bold', color: 'white' }}>
                        {calculateCurrentPercentage()}%
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                      <h2 style={{ margin: 0 }}>{activeProfile.name}</h2>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        <strong>{sections.reduce((acc, s) => acc + s.items.filter(i => i.state === 4).length, 0)}</strong> von {sections.reduce((acc, s) => acc + s.items.length, 0)} Übungen abgeschlossen
                      </p>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        <strong>{sections.reduce((acc, s) => acc + s.items.filter(i => i.state > 0 && i.state < 4).length, 0)}</strong> Übungen in Bearbeitung
                      </p>
                    </div>
                    <button 
                      className="btn btn-secondary btn-small" 
                      style={{ fontSize: '1rem', padding: '5px 8px' }}
                      onClick={() => { setEditedName(activeProfile.name); setIsEditingName(true); }}
                    >
                      📝
                    </button>
                  </>
                )}
              </div>
              
              {sections.map(section => (
                <Card 
                  key={section.id}
                  title={section.title}
                  items={section.items}
                  onUpdate={handleUpdateProgress}
                />
              ))}
            </div>
          )}
        </>
      )}

      {view === 'admin' && user.is_admin === 1 && <AdminPanel token={token} />}

      {view === 'settings' && (
        <div className="dashboard-card" style={{ maxWidth: '400px', margin: '30px auto' }}>
          <h3>Mein Profil / Einstellungen</h3>
          <p>Eingeloggt als: <strong>{user.username}</strong></p>
          <form onSubmit={handleChangePassword}>
            <div style={{ marginBottom: '15px' }}>
              <label>Neues Passwort</label>
              <input name="newPassword" type="password" className="input-field" required />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>Passwort ändern</button>
          </form>
        </div>
      )}

      {isModalOpen && (
        <Modal 
          title="Schüler löschen?"
          message={`Bist du sicher, dass du ${profiles.find(p => p.id === profileToDelete)?.name} löschen möchtest?`}
          onConfirm={confirmDeleteProfile}
          onCancel={() => setIsModalOpen(false)}
        />
      )}

      {error && <div className="error-toast">{error}</div>}
    </div>
  );
}

export default App;
